import { AnyObject } from '@data-driven-forms/react-form-renderer/dist/cjs/common';

import { EMAIL_REGEX, EMAIL_VALIDATION_MESSAGE, FIELD_NAMES, StepNames } from '../..';
import {
  DBFormSchema,
  fieldLocationAddSearchSection,
  fieldPlainTextSection,
  fieldPrescriberAddSearchSection,
  headerSection,
  separatorSection,
  fieldTextSection,
  spacerSection,
  titleSection,
  fieldSelectSection,
  fieldClaimTypeSelectSection,
  fieldPaymentTypeSelectSection,
  fieldPayeeTypeSelectSection,
  plainTextSection,
  fieldUploadSection,
  fieldSelectStatesSection
} from '../field-helpers';
import validatorTypes from '@data-driven-forms/react-form-renderer/dist/cjs/validator-types';

const { claimInformationFields, organizationFields, prescriberFields, patientFields } = FIELD_NAMES;

export const claimInformationTabFields = (schema: DBFormSchema, nextStep?: StepNames | AnyObject) => {
  const claimInformationTab = schema?.claim_information_tab;

  if (!claimInformationTab || claimInformationTab?.hideTab) return null;

  return {
    title: claimInformationTab?.label ?? 'Claim Information',
    name: StepNames.Claim_Information,
    nextStep: claimInformationTab?.nextStep ?? nextStep,
    fields: [
      {
        component: 'all-fields-required-note',
        name: 'claim'
      },
      headerSection(
        claimInformationFields.copayClaimInformationHeader,
        schema?.[claimInformationFields.copayClaimInformationHeader]
      ),
      spacerSection('claim_information_1', {
        sizes: { sm: 12 },
        className: 'mb-3'
      }),
      fieldTextSection(claimInformationFields.emailId, schema?.[claimInformationFields.emailId], {
        sizes: { md: 3 },
        validate: [
          {
            type: validatorTypes.PATTERN,
            pattern: EMAIL_REGEX,
            message: EMAIL_VALIDATION_MESSAGE
          }
        ],
        className: 'add-patient-text'
      }),
      fieldClaimTypeSelectSection(claimInformationFields.claimType, schema?.[claimInformationFields.claimType], {
        sizes: { md: 3 }
      }),
      fieldPaymentTypeSelectSection(
        claimInformationFields.paymentMethod,
        schema?.[claimInformationFields.paymentMethod],
        {
          sizes: { md: 3 }
        }
      ),
      fieldTextSection(claimInformationFields.claimAmount, schema?.[claimInformationFields.claimAmount], {
        sizes: { md: 3 },
        type: 'currency'
      }),
      spacerSection('claim_information_2', { sizes: { sm: 12 }, className: 'mb-3' }),
      headerSection(claimInformationFields.payeeHeader, schema?.[claimInformationFields.payeeHeader]),
      fieldPayeeTypeSelectSection(claimInformationFields.payeeType, schema?.[claimInformationFields.payeeType], {
        sizes: { md: 3 },
        className: 'mb-3'
      }),
      fieldTextSection(organizationFields.name, schema?.[claimInformationFields.claim_facility_name], {
        sizes: { md: 3 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationAddress1, schema?.[claimInformationFields.facility_address_1], {
        sizes: { md: 6 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationAddress2, schema?.[claimInformationFields.facility_address_2], {
        sizes: { md: 3 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationAddressCity, schema?.[prescriberFields.locationAddressCity], {
        sizes: { md: 3 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldSelectStatesSection(prescriberFields.locationAddressState, schema?.[prescriberFields.locationAddressState], {
        sizes: { md: 3 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationAddressZip, schema?.[prescriberFields.locationAddressZip], {
        sizes: { md: 3 },
        type: 'zip',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationPhone1, schema?.[prescriberFields.locationPhone1], {
        sizes: { md: 3 },
        placeholder: '(XXX) XXX-XXXX',
        type: 'tel',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(prescriberFields.locationFaxNumber, schema?.[prescriberFields.locationFaxNumber], {
        sizes: { md: 3 },
        placeholder: '(XXX) XXX-XXXX',
        type: 'tel',
        condition: {
          when: 'payee_type',
          is: 'Practice'
        }
      }),
      fieldTextSection(
        claimInformationFields.prescriber_first_name,
        schema?.[claimInformationFields.prescriber_first_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldTextSection(
        claimInformationFields.prescriber_middle_name,
        schema?.[claimInformationFields.prescriber_middle_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldTextSection(
        claimInformationFields.prescriber_last_name,
        schema?.[claimInformationFields.prescriber_last_name],
        {
          sizes: { md: 3 },
          condition: {
            when: 'payee_type',
            is: 'Provider'
          }
        }
      ),
      fieldTextSection(patientFields.firstName, schema?.[patientFields.firstName], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(patientFields.middleName, schema?.[patientFields.middleName], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(claimInformationFields.patient_last_name, schema?.[claimInformationFields.patient_last_name], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(claimInformationFields.patient_address_1, schema?.[claimInformationFields.patient_address_1], {
        sizes: { md: 3 },
        className: 'mb-3',
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(claimInformationFields.patient_address_2, schema?.[claimInformationFields.patient_address_2], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(claimInformationFields.claim_patient_city, schema?.[claimInformationFields.claim_patient_city], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldSelectStatesSection(claimInformationFields.patient_state, schema?.[claimInformationFields.patient_state], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      fieldTextSection(claimInformationFields.patient_zip, schema?.[claimInformationFields.patient_zip], {
        sizes: { md: 3 },
        condition: {
          when: 'payee_type',
          is: 'Patient'
        }
      }),
      spacerSection('claim_information_3', { sizes: { sm: 12 }, className: 'mb-3' }),
      headerSection(
        claimInformationFields.copayClaimSupportDocumentHeader,
        schema?.[claimInformationFields.copayClaimSupportDocumentHeader]
      ),
      plainTextSection(
        claimInformationFields.copayClaimSupportDocumentText,
        schema?.[claimInformationFields.copayClaimSupportDocumentText],
        {
          sizes: { md: 12 },
          style: { fontWeight: '400' }
        }
      ),
      plainTextSection(
        claimInformationFields.copayClaimSupportDocumentInfo,
        schema?.[claimInformationFields.copayClaimSupportDocumentInfo],
        {
          sizes: { md: 12 },
          style: { fontWeight: '400' }
        }
      ),
      fieldUploadSection(
        claimInformationFields.copayClaimSupportDocumentUpload,
        schema?.[claimInformationFields.copayClaimSupportDocumentUpload],
        {
          sizes: { md: 5 },
          className: 'add-patient-text',
          dropzoneOptions: { maxFiles: 3, maxSize: 10000000, accept: '.pdf' },
          showHelperText: true,
          showGenericFileName: true,
          showNewFileBadge: true,
          fileSizeError: schema?.[claimInformationFields.copayClaimSupportFileSizeError],
          fileTypeError: schema?.[claimInformationFields.copayClaimSupportFileTypeError],
          maxFilesError: schema?.[claimInformationFields.copayClaimSupportMaxFilesError]
        }
      )
    ].filter(Boolean)
  };
};
